import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ChevronDown, Ear, Sparkles, ShieldCheck, Check, X } from "lucide-react";
import { useAuth, BACKEND_URL } from "@/App";
import V1HeroSection from "@/components/landing/v1/V1HeroSection";
import V1FeatureSection from "@/components/landing/v1/V1FeatureSection";
import {
  V1_FAQS,
  V1_HOW_IT_WORKS,
  V1_VS_SHADOW,
  V1_VS_GENERIC,
} from "@/data/marketingHomeV1Content";

const LABS_PAGES = [
  { label: "Agent Control", path: "/agent-control", desc: "Okta for your AI agents" },
  { label: "Conversational Intelligence", path: "/conversational-intelligence", desc: "Deep analytics on your conversations" },
  { label: "Agent Margins", path: "/agent-margins", desc: "Tune and configure AI agents" },
  { label: "Sidequests", path: "/sidequests", desc: "Async task tracking from meetings" },
  { label: "Demo", path: "/demo", desc: "Interactive product walkthrough" },
];

function V1LabsAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-8 border-t border-slate-200 pt-6">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 group"
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
      >
        <span className="text-xs text-slate-400 tracking-widest uppercase group-hover:text-slate-500 transition-colors duration-200">
          Labs
        </span>
        <ChevronDown
          size={11}
          className="text-slate-400 group-hover:text-slate-500 transition-all duration-200"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      {open && (
        <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
          <li>
            <Link
              to="/"
              className="text-xs text-[#B55238] hover:text-[#7A3324] transition-colors duration-200 font-medium"
            >
              Classic homepage
            </Link>
          </li>
          {LABS_PAGES.map((p) => (
            <li key={p.path}>
              <Link
                to={p.path}
                title={p.desc}
                className="text-xs text-slate-400 transition-colors duration-200 hover:text-[#B55238]"
              >
                {p.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const howIcons = [Ear, Sparkles, ShieldCheck];

export default function MarketingHomeV1() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    window.location.href = `${BACKEND_URL}/api/auth/google`;
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  const navLink =
    "text-sm font-normal text-slate-600 hover:text-[#7A3324] transition-colors";

  return (
    <div className="v1-noise min-h-screen bg-[#FAF7F8] antialiased">
      <header className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div className="mx-auto max-w-5xl px-3 pt-4 sm:px-4 sm:pt-5 lg:max-w-6xl pointer-events-auto">
          <nav
            className="relative flex w-full items-center rounded-full border border-white/60 bg-white/70 px-3 py-2 shadow-lg shadow-slate-900/[0.04] backdrop-blur-xl backdrop-saturate-150 sm:px-5 sm:py-2.5"
            aria-label="Main"
          >
            <div className="flex min-w-0 flex-1 justify-start">
              <Link to="/" className="flex items-center gap-2 shrink-0">
                <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#B55238] sm:h-3 sm:w-3" />
                <span className="text-base font-bold tracking-tight text-slate-900 sm:text-[1.05rem]">
                  <span className="text-slate-900">SHADOW</span>
                  <span className="bg-gradient-to-r from-[#7A3324] to-[#B55238] bg-clip-text text-transparent">LABS</span>
                </span>
              </Link>
            </div>

            <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 md:flex items-center gap-4 lg:gap-7">
              <Link to="/demo" className={navLink}>
                Demo
              </Link>
              <button type="button" onClick={() => scrollToSection("how-it-works")} className={navLink}>
                How it works
              </button>
              <button type="button" onClick={() => scrollToSection("features")} className={navLink}>
                Features
              </button>
              <Link to="/pricing" className={navLink}>
                Pricing
              </Link>
              <button type="button" onClick={() => scrollToSection("connect")} className={navLink}>
                Connect
              </button>
            </div>

            <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="hidden md:inline-flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/80 px-4 py-2 text-sm font-semibold text-slate-900 transition-all duration-300 hover:border-[#B55238]/30 hover:bg-white hover:shadow-[0_0_20px_rgba(181,82,56,0.12)]"
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden>
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-slate-900 hover:bg-slate-50 md:hidden"
                aria-label="Menu"
                aria-expanded={mobileMenuOpen}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </nav>

          {mobileMenuOpen && (
            <div className="mt-2 rounded-3xl border border-white/60 bg-white/80 p-4 shadow-lg shadow-slate-900/[0.06] backdrop-blur-xl md:hidden">
              <div className="flex flex-col gap-1">
                <Link
                  to="/demo"
                  className="rounded-xl px-3 py-2.5 text-sm text-slate-600 hover:bg-[#B55238]/[0.06] hover:text-[#7A3324]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Demo
                </Link>
                <button
                  type="button"
                  onClick={() => scrollToSection("how-it-works")}
                  className="rounded-xl px-3 py-2.5 text-left text-sm text-slate-600 hover:bg-[#B55238]/[0.06] hover:text-[#7A3324]"
                >
                  How it works
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection("features")}
                  className="rounded-xl px-3 py-2.5 text-left text-sm text-slate-600 hover:bg-[#B55238]/[0.06] hover:text-[#7A3324]"
                >
                  Features
                </button>
                <Link
                  to="/pricing"
                  className="rounded-xl px-3 py-2.5 text-sm text-slate-600 hover:bg-[#B55238]/[0.06] hover:text-[#7A3324]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
                <button
                  type="button"
                  onClick={() => scrollToSection("connect")}
                  className="rounded-xl px-3 py-2.5 text-left text-sm text-slate-600 hover:bg-[#B55238]/[0.06] hover:text-[#7A3324]"
                >
                  Connect
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleGoogleLogin();
                  }}
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 py-2.5 text-sm font-semibold text-slate-900 hover:border-[#B55238]/30"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <V1HeroSection handleGoogleLogin={handleGoogleLogin} />

      {/* How it works */}
      <section
        id="how-it-works"
        className="relative border-t border-rose-100/40 bg-gradient-to-b from-[#FAF7F8] via-white to-white py-16 sm:py-24 px-4 sm:px-8 overflow-hidden"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 40% at 50% 20%, rgba(181,82,56,0.03), transparent 60%)" }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto mb-10 max-w-2xl text-center sm:mb-14"
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#B55238]">How it works</p>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Before, during, and after-in one flow
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
              From prep to live guidance to CRM updates - in one flow.
            </p>
          </motion.div>

          <div className="relative grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-6 lg:gap-8">
            {/* Connecting line on desktop */}
            <div className="absolute top-16 left-[16.67%] right-[16.67%] hidden h-px md:block" aria-hidden>
              <motion.div
                className="h-full w-full bg-gradient-to-r from-transparent via-[#B55238]/20 to-transparent"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </div>

            {V1_HOW_IT_WORKS.map((item, i) => {
              const Icon = howIcons[i];
              return (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 24, rotate: 1 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, type: "spring", stiffness: 260, damping: 25 }}
                  className="v1-gradient-border group relative rounded-2xl border border-white/60 bg-white/60 p-8 shadow-sm ring-1 ring-slate-100/50 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:shadow-slate-900/[0.04]"
                >
                  <motion.div
                    className="mb-4 inline-flex items-center rounded-full border border-[#B55238]/20 bg-[#B55238]/10 px-2.5 py-1"
                    whileInView={{ boxShadow: ["0 0 0 0 rgba(181,82,56,0)", "0 0 16px 2px rgba(181,82,56,0.15)", "0 0 8px 0 rgba(181,82,56,0.08)"] }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.4 + i * 0.15 }}
                  >
                    <span className="font-mono text-[11px] font-semibold tabular-nums text-[#7A3324]">{item.step}</span>
                  </motion.div>
                  <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl border border-teal-200/80 bg-teal-50/80 transition-shadow duration-300 group-hover:shadow-[0_0_16px_-2px_rgba(20,184,166,0.25)]">
                    <Icon className="h-5 w-5 text-teal-600" strokeWidth={1.75} />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold tracking-tight text-slate-900">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features intro */}
      <section
        id="features"
        className="relative pt-8 pb-4 px-4 sm:px-8 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(245,241,237,0.7) 0%, rgba(250,247,248,0.5) 50%, rgba(240,253,250,0.3) 100%)",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 50% 60% at 20% 30%, rgba(181,82,56,0.04), transparent 60%), radial-gradient(ellipse 40% 50% at 80% 70%, rgba(20,184,166,0.03), transparent 60%)" }}
          aria-hidden
        />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative max-w-6xl mx-auto text-center"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#B55238]">Capabilities</p>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Built for the moments that move deals forward
          </h2>
        </motion.div>
      </section>

      <V1FeatureSection />

      {/* vs Generic AI */}
      <section className="relative py-16 sm:py-20 px-4 sm:px-8 bg-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 50% 50% at 25% 50%, rgba(181,82,56,0.025), transparent 60%)" }}
          aria-hidden
        />
        <div className="relative max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#B55238]">Why not generic AI?</p>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Generic AI misses the deal. Shadow doesn&apos;t.
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-2xl border-2 border-[#B55238]/20 bg-[#B55238]/[0.05] p-6 sm:p-8 shadow-[0_0_32px_-8px_rgba(181,82,56,0.10)] backdrop-blur-sm">
              <h3 className="mb-6 text-sm font-semibold uppercase tracking-wide text-[#7A3324]">Shadow</h3>
              <ul className="space-y-4">
                {V1_VS_SHADOW.map((line, i) => (
                  <motion.li
                    key={line}
                    className="flex gap-3 text-sm text-slate-800"
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08, type: "spring", stiffness: 300, damping: 25 }}
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 + 0.1, type: "spring", stiffness: 500, damping: 20 }}
                    >
                      <Check className="w-5 h-5 text-[#B55238] flex-shrink-0" strokeWidth={2} />
                    </motion.span>
                    <span>{line}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-6 sm:p-8 backdrop-blur-sm">
              <h3 className="mb-6 text-sm font-semibold uppercase tracking-wide text-slate-500">Generic AI</h3>
              <ul className="space-y-4">
                {V1_VS_GENERIC.map((line, i) => (
                  <motion.li
                    key={line}
                    className="flex gap-3 text-sm text-slate-600"
                    initial={{ opacity: 0, x: 12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08, type: "spring", stiffness: 300, damping: 25 }}
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 + 0.1, type: "spring", stiffness: 500, damping: 20 }}
                    >
                      <X className="w-5 h-5 text-slate-400 flex-shrink-0" strokeWidth={2} />
                    </motion.span>
                    <span>{line}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="relative py-16 sm:py-20 px-4 sm:px-8 bg-gradient-to-b from-slate-50/80 to-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 50% 50% at 50% 40%, rgba(181,82,56,0.025), transparent 60%)" }}
          aria-hidden
        />
        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#B55238]">Your stack</p>
            <h2 className="mb-10 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Works with every meeting platform
            </h2>
          </motion.div>
          <motion.div
            className="mb-12 rounded-2xl border border-white/60 bg-white/50 p-6 shadow-sm backdrop-blur-sm"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <img src="/images/image 8.png" alt="Meeting platforms" className="mx-auto max-w-full sm:max-w-md" />
          </motion.div>
          <motion.div
            className="rounded-2xl border border-white/60 bg-white/50 p-6 shadow-sm backdrop-blur-sm"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <img src="/images/image 9.png" alt="Integrations" className="mx-auto max-w-full sm:max-w-4xl" />
          </motion.div>
        </div>
      </section>

      {/* Demo CTA */}
      <section className="py-14 sm:py-16 px-4 sm:px-8 bg-gradient-to-b from-white to-slate-50/80">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#5c2618] via-[#7A3324] to-[#B55238] p-8 sm:rounded-3xl sm:p-12 shadow-[0_0_60px_-8px_rgba(181,82,56,0.25)] ring-1 ring-white/10"
          >
            {/* Floating particles */}
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute h-1 w-1 rounded-full bg-white/30"
                style={{ left: `${15 + i * 18}%`, top: `${20 + (i % 3) * 25}%` }}
                animate={{ y: [0, -20, 0], opacity: [0.2, 0.5, 0.2] }}
                transition={{ duration: 4 + i * 0.8, repeat: Infinity, delay: i * 0.6, ease: "easeInOut" }}
                aria-hidden
              />
            ))}
            {/* Animated gradient overlay */}
            <div
              className="absolute inset-0 opacity-30 animate-gradient-shimmer pointer-events-none"
              style={{ background: "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)", backgroundSize: "200% auto" }}
              aria-hidden
            />
            <div className="relative">
              <h2 className="mb-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">
                See Shadow in a live meeting demo
              </h2>
              <p className="text-sm text-white/80 sm:text-base mb-6 max-w-lg mx-auto leading-relaxed">
                No sign-up required. Watch how Shadow surfaces real-time fact-checks, counter-arguments, and follow-ups during a simulated meeting.
              </p>
              <Link
                to="/demo"
                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-white px-8 py-3.5 text-sm font-semibold text-[#7A3324] shadow-lg transition-all hover:bg-rose-50 hover:scale-[1.03] hover:shadow-xl"
              >
                <Play className="w-4 h-4" />
                See it in action
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 px-4 sm:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#B55238]">Questions</p>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">FAQs</h2>
            </motion.div>
            <div className="space-y-3 sm:space-y-4">
              {V1_FAQS.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.04 }}
                  className={`rounded-xl border border-transparent px-4 py-4 sm:px-5 sm:py-5 transition-all duration-200 ${
                    openFaq === index ? "border-slate-100 bg-white/70 shadow-sm backdrop-blur-sm" : "hover:bg-slate-50/60"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="group flex w-full items-center justify-between text-left"
                  >
                    <h3 className="pr-4 text-sm font-semibold tracking-tight text-slate-900 sm:text-base transition-colors group-hover:text-[#7A3324]">
                      {faq.question}
                    </h3>
                    <motion.svg
                      className="w-5 h-5 flex-shrink-0 text-slate-500 group-hover:text-[#B55238]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      animate={{ rotate: openFaq === index ? 180 : 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </motion.svg>
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3 sm:pt-4 pr-8">
                          <p className="text-sm leading-relaxed text-slate-600">{faq.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Connect */}
      <section
        id="connect"
        className="relative py-16 sm:py-20 px-4 sm:px-8 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(248,246,244,0.8) 0%, rgba(250,247,248,0.6) 50%, rgba(245,248,250,0.5) 100%)",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 50% 50% at 50% 60%, rgba(181,82,56,0.025), transparent 60%)" }}
          aria-hidden
        />
        <div className="relative max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-14"
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#B55238]">Contact</p>
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Get in touch
            </h2>
            <p className="mx-auto max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
              Have questions about Shadow, pricing, or how it fits your sales workflow? Our team is here to help.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mx-auto max-w-xl"
          >
            <div className="v1-gradient-border rounded-2xl border border-white/60 bg-white/70 p-6 shadow-sm backdrop-blur-md sm:p-10">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target;
                  const name = form.elements.name.value;
                  const email = form.elements.email.value;
                  const message = form.elements.message.value;
                  const text = `Hi, I'm ${name} (${email}). ${message}`;
                  window.open(`https://wa.me/919582819710?text=${encodeURIComponent(text)}`, "_blank");
                }}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
                    <input
                      name="name"
                      type="text"
                      placeholder="Your name"
                      required
                      className="w-full rounded-lg border border-slate-200/80 bg-white/80 px-4 py-3 text-sm transition-all focus:border-[#B55238] focus:outline-none focus:ring-2 focus:ring-[#B55238]/20 focus:shadow-[0_0_12px_-2px_rgba(181,82,56,0.12)]"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Email</label>
                    <input
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      required
                      className="w-full rounded-lg border border-slate-200/80 bg-white/80 px-4 py-3 text-sm transition-all focus:border-[#B55238] focus:outline-none focus:ring-2 focus:ring-[#B55238]/20 focus:shadow-[0_0_12px_-2px_rgba(181,82,56,0.12)]"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Message</label>
                  <textarea
                    name="message"
                    placeholder="How can we help?"
                    rows={4}
                    required
                    className="w-full resize-none rounded-lg border border-slate-200/80 bg-white/80 px-4 py-3 text-sm transition-all focus:border-[#B55238] focus:outline-none focus:ring-2 focus:ring-[#B55238]/20 focus:shadow-[0_0_12px_-2px_rgba(181,82,56,0.12)]"
                  />
                </div>
                <button
                  type="submit"
                  className="group relative w-full overflow-hidden rounded-lg bg-[#B55238] py-3 text-sm font-semibold text-white shadow-lg shadow-[#B55238]/25 transition-all duration-300 hover:scale-[1.02] hover:bg-[#9A452C] hover:shadow-xl hover:shadow-[#7A3324]/15"
                >
                  <span className="absolute inset-0 overflow-hidden rounded-lg">
                    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent group-hover:animate-[btn-shimmer_0.8s_ease-out_forwards]" />
                  </span>
                  <span className="relative">Send a message</span>
                </button>
              </form>
            </div>
            <p className="mt-5 text-center text-xs leading-relaxed text-slate-400">
              Or email us directly at{" "}
              <a href="mailto:hello@shadowlabs.ai" className="text-slate-500 transition-colors hover:text-[#B55238] v1-link-underline">
                hello@shadowlabs.ai
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      <footer className="relative py-8 sm:py-12 px-4 sm:px-8 bg-white/50 backdrop-blur-sm">
        {/* Gradient top border */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" aria-hidden />
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-8">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="h-2 w-2 rounded-full bg-[#B55238]" />
                <span className="text-sm font-semibold tracking-tight">
                  <span className="text-slate-800">SHADOW</span>
                  <span className="bg-gradient-to-r from-[#7A3324] to-[#B55238] bg-clip-text text-transparent">LABS</span>
                </span>
              </Link>
              <p className="text-xs text-slate-500 max-w-xs">
                Real-time cognition for account executives-Smart Cards when the deal is live.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Navigation</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>
                  <Link to="/demo" className="v1-link-underline transition-colors hover:text-[#B55238]">
                    Demo
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => scrollToSection("features")}
                    className="v1-link-underline text-left transition-colors hover:text-[#B55238]"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <Link to="/pricing" className="v1-link-underline transition-colors hover:text-[#B55238]">
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="v1-link-underline transition-colors hover:text-[#B55238]">
                    Blog
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => scrollToSection("connect")}
                    className="v1-link-underline text-left transition-colors hover:text-[#B55238]"
                  >
                    Contact
                  </button>
                </li>
                <li>
                  <Link to="/" className="v1-link-underline text-[#B55238] hover:text-[#7A3324]">
                    Classic homepage
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>
                  <Link to="/privacy" className="v1-link-underline transition-colors hover:text-[#B55238]">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="v1-link-underline transition-colors hover:text-[#B55238]">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <V1LabsAccordion />
          <div className="relative mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" aria-hidden />
            <p className="text-xs text-slate-500">&copy; 2026 ShadowLabs. All rights reserved.</p>
            <a
              href="https://www.producthunt.com/products/shadow-6?utm_source=badge-follow&utm_medium=badge&utm_source=badge-shadow-6"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://api.producthunt.com/widgets/embed-image/v1/follow.svg?product_id=1175154&theme=light"
                alt="Shadow on Product Hunt"
                width={200}
                height={43}
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
